import { useState } from "preact/hooks";
import { ApiError, api } from "../api/client.ts";
import type { CommitDetail, Commit as CommitOut, Conversation, PageChangeView, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { useConversation } from "../lib/conversation.ts";
import { canonical, diff } from "../lib/diff.ts";
import { formatInstant } from "../lib/format.ts";
import { LINKS, summary } from "../lib/history.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { type Resource, useAction, useResource } from "../lib/resource.ts";
import { canEdit } from "../lib/roles.ts";
import { navigate, tabHref, tabParam } from "../lib/router.ts";
import type { PageProps } from "../lib/state.ts";
import { toast } from "../lib/toast.ts";
import { Person } from "../ui/Avatar.tsx";
import { BookmarkButton } from "../ui/BookmarkButton.tsx";
import { Button, Link } from "../ui/Button.tsx";
import { ConfirmButton } from "../ui/Dialog.tsx";
import { Async, Badge } from "../ui/misc.tsx";
import { PageHeader } from "../ui/PageHeader.tsx";
import { LINK } from "../ui/styles.ts";
import { TabNav } from "../ui/TabNav.tsx";
import { ConversationLabel, SimpleConversation } from "./comments/conversations.tsx";
import { DocumentChanges } from "./history/documentChange.tsx";
import { WithEditors } from "./history/editors.tsx";
import { Via } from "./settings/activity.tsx";

/** One object a commit changed, before and after. */
type ChangeView = PageChangeView["data"][number];

/** How many of a commit's changes a page shows. */
const CHANGES_PAGE = 50;

const show = (v: unknown) => (v === undefined ? "" : canonical(v));

/** Commit views redact other people's private items as `{"private": true}`. */
const redacted = (v: unknown) =>
  typeof v === "object" &&
  v !== null &&
  (v as Record<string, unknown>)["private"] === true &&
  Object.keys(v).length === 1;

function objectLabel(c: ChangeView): string {
  if (redacted(c.before) || redacted(c.after)) {
    return t("history.private");
  }
  const doc = (c.after ?? c.before ?? {}) as Record<string, unknown>;
  if (c.kind === "review") {
    return t("item.findings", { count: Array.isArray(doc["issues"]) ? doc["issues"].length : 0 });
  }
  const name = doc["title"] ?? doc["name"];
  return typeof name === "string" && name !== "" ? name : c.id;
}

function opOf(c: ChangeView): "created" | "deleted" | "updated" {
  if (c.before === null) {
    return "created";
  }
  return c.after === null ? "deleted" : "updated";
}

function DiffTable({ c }: { c: ChangeView }) {
  return (
    <table class="w-full table-fixed text-xs">
      <thead class="sr-only">
        <tr>
          <th scope="col">{t("history.path")}</th>
          <th scope="col">{t("history.before")}</th>
          <th scope="col">{t("history.after")}</th>
        </tr>
      </thead>
      <tbody>
        {diff(c.before, c.after).map((r) => (
          <tr key={r.path} class="border-t border-zinc-100 first:border-t-0">
            <td class="w-1/4 px-4 py-1.5 align-top font-mono text-zinc-600">{r.path}</td>
            <td class="px-2 py-1.5 align-top font-mono break-all text-red-800">
              {r.before === undefined ? null : <del class="bg-red-50 no-underline">{show(r.before)}</del>}
            </td>
            <td class="px-2 py-1.5 align-top font-mono break-all text-green-800">
              {r.after === undefined ? null : <ins class="bg-green-50 no-underline">{show(r.after)}</ins>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Change({ space, c }: { space: SpaceOut; c: ChangeView }) {
  const op = opOf(c);
  const hidden = redacted(c.before) || redacted(c.after);
  const link = hidden ? null : LINKS[c.kind];
  return (
    <li class="rounded-lg border border-zinc-200 bg-white">
      <div class="flex flex-wrap items-center gap-2 border-b border-zinc-200 px-4 py-2 text-sm">
        <Badge tone="blue">{t(`kind.${c.kind}`)}</Badge>
        {link && op !== "deleted" ? (
          <Link href={spaceUrl(space, `${link}${c.id}`)} class={LINK}>
            {objectLabel(c)}
          </Link>
        ) : (
          <span class="font-medium">{objectLabel(c)}</span>
        )}
        <span class="text-zinc-500">{t(`history.op.${op}`)}</span>
      </div>
      {hidden ? <p class="px-4 py-2 text-xs text-zinc-500 italic">{t("history.private")}</p> : <DiffTable c={c} />}
    </li>
  );
}

interface Blocked {
  message: string;
  commits: string[];
}

function Blocking({ space, blocked }: { space: SpaceOut; blocked: Blocked | null }) {
  return blocked ? (
    <div role="alert" class="mb-6 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800">
      <p>{blocked.message}</p>
      <ul class="mt-2 list-inside list-disc">
        {blocked.commits.map((b) => (
          <li key={b}>
            <Link href={spaceUrl(space, `/history/${b}`)} class="font-medium underline">
              {t("history.commit", { id: b })}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  ) : null;
}

/** Reverts a commit; a `revert_conflict` (later commits touched the same objects) is shown with links to them. */
function useRevert(space: SpaceOut, id: string) {
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  const [revert, busy] = useAction(async () => {
    setBlocked(null);
    try {
      const c = await api.post<CommitOut>(spaceApi(space, `/commits/${id}/revert`));
      toast(t("history.reverted"));
      navigate(spaceUrl(space, `/history/${c.id}`));
    } catch (e) {
      if (e instanceof ApiError && e.code === "revert_conflict") {
        const commits = e.info["commits"];
        setBlocked({ message: e.message, commits: Array.isArray(commits) ? commits.map(String) : [] });
        return;
      }
      throw e;
    }
  });
  return { blocked, revert, busy };
}

/** A change's page tab from `?tab=`: what it changed, or its conversation. */
const tabOf = () => tabParam(["changes", "conversation"]);

/** A change's tabs: what it changed and its conversation. */
function CommitTabs({ space, commit, conv }: { space: SpaceOut; commit: string; conv: Resource<Conversation> }) {
  const base = spaceUrl(space, `/history/${commit}`);
  const tab = tabOf();
  return (
    <TabNav
      label={t("history.tabs")}
      tabs={[
        { href: tabHref(base, null), label: t("history.tab.changes"), current: tab === "changes" },
        {
          href: tabHref(base, "conversation"),
          label: <ConversationLabel conv={conv} />,
          current: tab === "conversation",
        },
      ]}
    />
  );
}

/**
 * What a commit changed, a page at a time (a bulk change may hold thousands): the first page came with it, "Load
 * more" adds the next.
 */
function Changes({ space, id, first }: { space: SpaceOut; id: string; first: PageChangeView }) {
  const [pages, setPages] = useState<readonly PageChangeView[]>([first]);
  const shown = pages.flatMap((p) => p.data);
  const next = pages.at(-1)?.next;
  const [more, loading] = useAction(async (cursor: string) => {
    const d = await api.get<CommitDetail>(spaceApi(space, `/commits/${id}`), { cursor, limit: CHANGES_PAGE });
    setPages((had) => [...had, d.diff]);
  });
  const total = first.total ?? shown.length;
  return (
    <>
      <ul class="flex flex-col gap-4">
        {shown.map((ch) => (
          <Change key={ch.id} space={space} c={ch} />
        ))}
      </ul>
      {next ? (
        <div class="mt-4 flex items-center gap-3 text-sm text-zinc-600 tabular-nums">
          <span>{t("history.shown", { count: total, shown: shown.length })}</span>
          <Button busy={loading} onClick={() => more(next)}>
            {t("history.loadMore")}
          </Button>
        </div>
      ) : null}
    </>
  );
}

export function Commit({ space, params }: PageProps) {
  const id = params["id"] ?? "";
  const r = useResource(`commit:${space.id}:${id}`, (signal) =>
    api.get<CommitDetail>(spaceApi(space, `/commits/${id}`), { limit: CHANGES_PAGE }, signal),
  );
  const { blocked, revert, busy } = useRevert(space, id);
  const conv = useConversation(space, { commit: id });
  return (
    <Async r={r}>
      {(c) => (
        <>
          <PageHeader title={summary(c, c.changes)} id={c.id}>
            <BookmarkButton space={space} target={{ kind: "commit", commit: c.id }} size="md" variant="secondary" />
            {canEdit(space) ? (
              <ConfirmButton
                label={t("history.revertButton")}
                title={t("history.revertTitle")}
                message={t("history.revertMessage")}
                confirmLabel={t("history.revertButton")}
                busy={busy}
                onConfirm={revert}
              />
            ) : null}
          </PageHeader>
          <p class="-mt-4 mb-6 flex flex-wrap gap-3 text-sm text-zinc-600">
            {c.author_name ? <Person name={c.author_name} id={c.author} /> : <span>{t("history.system")}</span>}
            <WithEditors c={c} full={true} />
            {c.on_behalf_name ? <span>{t("history.onBehalf", { name: c.on_behalf_name })}</span> : null}
            <Via c={c.client} />
            <span>{formatInstant(c.created_at)}</span>
            {c.reverts ? (
              <Link href={spaceUrl(space, `/history/${c.reverts}`)} class={LINK}>
                {t("history.reverts")}
              </Link>
            ) : null}
          </p>
          <CommitTabs space={space} commit={c.id} conv={conv} />
          {tabOf() === "conversation" ? (
            <SimpleConversation space={space} topic={{ commit: c.id }} conv={conv} />
          ) : (
            <>
              <Blocking space={space} blocked={blocked} />
              {/* A document's file changed: the version before and the one it made, drawn side by side. */}
              <DocumentChanges key={`doc-${c.id}`} space={space} commit={c.id} first={c.diff} />
              <Changes key={c.id} space={space} id={c.id} first={c.diff} />
            </>
          )}
        </>
      )}
    </Async>
  );
}
